import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { api, errText, withRefresh } from "@/lib/auth";
import { Reveal } from "./Reveal";

const MAX = 280;

export const ReflectionWall = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    if (!process.env.REACT_APP_BACKEND_URL) return undefined;
    let alive = true;
    api.get("/reflections")
      .then(({ data }) => alive && setItems(Array.isArray(data) ? data : []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);
  if (!items.length) return null;
  return (
    <section data-testid="reflection-wall" className="max-w-[1440px] mx-auto px-5 sm:px-10 pb-28">
      <p className="font-tt-mono text-[12px] uppercase tracking-[0.35em] text-[#D9B47A]">Words from within the temple</p>
      <div className="mt-10 columns-1 md:columns-2 lg:columns-3 gap-6">
        {items.map((r, k) => (
          <Reveal key={r.id} y={20} delay={(k % 3) * 0.08} className="break-inside-avoid mb-6">
            <figure data-testid={`reflection-${r.id}`} className="border border-[#D9B47A]/20 bg-[#0D0908] p-8">
              <span className="font-display text-6xl leading-none text-[#C2414F]">“</span>
              <blockquote className="font-display text-xl sm:text-2xl leading-snug text-[#F2E8DF] -mt-4">{r.text}</blockquote>
              <figcaption className="mt-6 font-tt-mono text-[12px] uppercase tracking-[0.25em] text-[#A8998C]">— An Initiate</figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </section>
  );
};

const STATUS = {
  pending: "Awaiting Goddess Ariel’s review",
  approved: "Shared anonymously on the initiation page",
  rejected: "Kept private — not shared publicly",
};

export const ReflectionForm = ({ gate = null, prompt = "", className = "mt-16 max-w-xl mx-auto" }) => {
  const [mine, setMine] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let alive = true;
    withRefresh(() => api.get("/reflections/mine", { params: gate === null ? {} : { gate } }))
      .then(({ data }) => {
        if (!alive || !data) return;
        setMine(data);
        setText(data.text ?? "");
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [gate]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await withRefresh(() => api.post("/reflections", { text, gate }));
      setMine(data);
    } catch (err) {
      setError(errText(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} data-testid={gate === null ? "reflection-form" : "gate-reflection-form"} className={`w-full text-left border border-[#D9B47A]/25 bg-[#0D0908] p-6 sm:p-8 ${className}`}>
      <p className="font-tt-mono text-[12px] uppercase tracking-[0.3em] text-[#D9B47A]">{gate === null ? "Share a reflection" : "A reflection prompt"}</p>
      {prompt ? (
        <p data-testid="reflection-prompt" className="mt-4 font-display italic text-2xl leading-snug text-[#F2E8DF]">{prompt}</p>
      ) : null}
      <p className="mt-3 text-sm text-[#CFC2B6]">{prompt ? "If you wish, answer in a few words." : "A few words on what the gates have opened in you."} If Goddess Ariel approves it, it is shown anonymously as “An Initiate”.</p>
      <textarea data-testid="reflection-input" required minLength={10} maxLength={MAX} rows={4} value={text} onChange={(e) => setText(e.target.value)} placeholder="Since walking the gates…" className="mt-6 w-full bg-transparent border border-[#D9B47A]/25 p-4 text-base placeholder:text-[#8F8176] focus:outline-none focus:border-[#D9B47A] transition-colors resize-none" />
      <div className="mt-2 flex justify-between gap-4 text-[12px]">
        <span data-testid="reflection-status" className="text-[#D9B47A]">{mine ? STATUS[mine.status] ?? "" : ""}</span>
        <span data-testid="reflection-count" className="font-tt-mono text-[#A8998C]">{text.length}/{MAX}</span>
      </div>
      {error && <p data-testid="reflection-error" className="mt-3 text-sm text-[#E8838E]">{error}</p>}
      <button data-testid="reflection-submit" disabled={busy || text.trim().length < 10} className="mt-6 inline-flex items-center gap-3 rounded-full bg-[#D9B47A] text-[#0A0706] px-6 py-3 font-tt-mono text-[12px] uppercase tracking-[0.2em] hover:bg-[#F2E8DF] transition-colors disabled:opacity-50">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}{mine ? "Update reflection" : "Offer reflection"}
      </button>
    </form>
  );
};
